import { nullSafeGet } from '../../utils/DataAccessUtils';
import {
	getNewNotePayload,
	getPageEnterOrExitPayload,
	getTypingPayload,
	getUpdatedTypingDetails,
	notify,
	notifyGentle,
	WEB_SOCKET_ACTION_TYPES,
	WEB_SOCKET_ENTITY_TYPES,
	WEB_SOCKET_MESSAGE_TYPES,
	WebSocketPayloadProps,
} from '../../utils/WebSocketUtils';
import throttle from 'lodash/throttle';
import debounce from 'lodash/debounce';

const TYPING_EVENT_INTERVAL = 2000;
const TYPING_STOP_INACTIVITY_THRESHOLD = 2000;
const TITLE_BLINKING_INTERVAL = 1000;
export default class WebSocketTypingManager {
	previousSocket: any;
	cleanupInterval: any;
	component: any;
	socketProvider: any;
	getEntityIdsFromProps: any;
	getCurrentEntityIdFromProps: any;
	entityIdName: any;
	entityType: any;
	newNoteEntityType: any;
	typing = false;
	blinkInterval = null;
	originalTitle = document.title;
	isTabActive = true;
	isScrolledToBottom = false;
	rafId = null;

	joinSentForSocket: any = null;
	joinInFlightForSocket: any = null;
	joinPromise: Promise<any> | null = null;
	chatBoxElementId: string;

	constructor({
		component,
		socketProvider,
		getEntityIdsFromProps,
		getCurrentEntityIdFromProps,
		entityIdName,
		entityType,
		newNoteEntityType,
		chatBoxElementId = 'chatBox',
	}) {
		this.component = component;
		this.socketProvider = socketProvider;
		this.getEntityIdsFromProps = getEntityIdsFromProps;
		this.getCurrentEntityIdFromProps = getCurrentEntityIdFromProps;
		this.entityIdName = entityIdName;
		this.entityType = entityType;
		this.newNoteEntityType = newNoteEntityType;
		this.chatBoxElementId = chatBoxElementId;
	}

	_getPayloadProps = (): WebSocketPayloadProps => ({
		entityIdName: this.entityIdName,
		entityId: this.getCurrentEntityIdFromProps(this.component.props),
		currentUser: nullSafeGet('props.currentUser', this.component),
		entityType: this.entityType,
		newNoteEntityType: this.newNoteEntityType,
	});

	_sendTypingEvent = () => {
		this.sendTyping();
	};

	_sendTypingStopEvent = () => {
		this.typing = false;
		this.sendTypingStop();
	};

	throttledJoin = throttle(
		() => {
			this.ensurePageEnter();
		},
		TYPING_EVENT_INTERVAL,
		{ leading: true, trailing: false }
	);

	throttledSendTyping = throttle(
		() => {
			this._sendTypingEvent();
		},
		TYPING_EVENT_INTERVAL,
		{ leading: true, trailing: false }
	);

	debouncedStopTyping = debounce(() => {
		this._sendTypingStopEvent();
	}, TYPING_STOP_INACTIVITY_THRESHOLD);

	startBlinkingTitle = (message = 'New Notification') => {
		if (this.isTabActive) return;

		const moreThanOneNote = nullSafeGet('component.state.newNoteCount', this) > 0;
		//Notify with chime sound only when tab is not active
		moreThanOneNote ? notifyGentle() : notify();

		if (this.blinkInterval) return; // avoid duplicates

		let toggle = false;

		this.blinkInterval = setInterval(() => {
			document.title = toggle ? `${message} 🔔` : this.originalTitle;
			toggle = !toggle;
		}, TITLE_BLINKING_INTERVAL);
	};

	stopBlinkingTitle = () => {
		if (this.blinkInterval) {
			clearInterval(this.blinkInterval);
			this.blinkInterval = null;
		}
		document.title = this.originalTitle;
	};

	setupVisibilityListener = () => {
		document.addEventListener('visibilitychange', this.handleVisibilityChange);
	};

	cleanupVisibilityListener = () => {
		document.removeEventListener('visibilitychange', this.handleVisibilityChange);
	};

	handleVisibilityChange = () => {
		this.isTabActive = document.visibilityState === 'visible';

		if (this.isTabActive) {
			this.stopBlinkingTitle();
		}
	};

	attachListener = () => {
		const socketContext = this.socketProvider();
		const { socket } = socketContext;

		if (!socket) return;

		if (this.previousSocket !== socket) {
			if (this.previousSocket) {
				this.previousSocket.removeEventListener('message', this.handleMessage);
			}
			socket.removeEventListener('message', this.handleMessage);
			socket.addEventListener('message', this.handleMessage);
			this.previousSocket = socket;
		}
		this.throttledJoin();
	};

	startCleanup = () => {
		if (this.cleanupInterval) clearInterval(this.cleanupInterval);

		this.cleanupInterval = setInterval(() => {
			this.component.setState((prev) => {
				const updatedTypingDetails = getUpdatedTypingDetails(prev.typingDetails, null);
				return { typingDetails: updatedTypingDetails };
			});
		}, 1000);
	};

	stopCleanup = () => {
		clearInterval(this.cleanupInterval);
		this.cleanupInterval = null;
	};

	updateNewNoteCountOrRefreshMessages = () => {
		const { component } = this;

		if (this.isTabActive && this.isScrolledToBottom) {
			component.refreshMessages();
		} else {
			component.setState((prev) => {
				const prevNewNoteCount = prev.newNoteCount || 0;
				return { newNoteCount: prevNewNoteCount + 1 };
			});
		}
	};

	handleMessage = (event) => {
		const { component } = this;

		try {
			const data = JSON.parse(event.data);

			// Scope guard. Regular-notes listeners stay permissive when data.entityType
			// is absent so old clients (pre-rollout) that broadcast without it are still
			// accepted. Internal-notes listeners must be strict: a missing entityType
			// means the broadcast came from a regular-note sender on an old client, and
			// processing it would mis-trigger an internal-notes refresh or badge.
			const isInternalNotesScope =
				this.entityType === WEB_SOCKET_ENTITY_TYPES.WORK_ORDER_INTERNAL_NOTES;
			if (isInternalNotesScope) {
				if (data.entityType !== this.entityType) return;
			} else if (
				data.entityType &&
				data.entityType !== this.entityType &&
				data.entityType !== this.newNoteEntityType
			) {
				return;
			}

			const entityId = this.getCurrentEntityIdFromProps(this.component.props);

			if (data[this.entityIdName] != entityId) return;

			switch (data.messageType) {
				case WEB_SOCKET_MESSAGE_TYPES.NEW_NOTE:
					this.startBlinkingTitle('New Note');
					this.updateNewNoteCountOrRefreshMessages();
					break;

				case WEB_SOCKET_MESSAGE_TYPES.NOTE_TYPING:
					component.setState((prev) => {
						const updatedTypingDetails = getUpdatedTypingDetails(
							prev.typingDetails,
							nullSafeGet('typingBy', data)
						);
						return { typingDetails: updatedTypingDetails };
					});
					break;
				case WEB_SOCKET_MESSAGE_TYPES.NOTE_TYPING_STOP:
					component.setState((prev) => {
						const updatedTypingDetails = prev.typingDetails.filter(
							(detail) => detail.email !== nullSafeGet('typingBy.email', data)
						);
						return { typingDetails: updatedTypingDetails };
					});
					break;

				default:
					break;
			}
		} catch (err) {
			console.log('JSON parse error', err);
		}
	};

	sendTypingEvent = (messageType) => {
		const broadCastData = getTypingPayload({
			...this._getPayloadProps(),
			messageType,
		});

		this.sendMessageIfSocketExists(broadCastData);
	};

	sendTyping = () => {
		this.sendTypingEvent(WEB_SOCKET_MESSAGE_TYPES.NOTE_TYPING);
	};

	sendTypingStop = () => {
		this.sendTypingEvent(WEB_SOCKET_MESSAGE_TYPES.NOTE_TYPING_STOP);
	};

	handleTyping = () => {
		if (!this.typing) {
			this.typing = true;
			this._sendTypingEvent();
		}

		this.throttledSendTyping();
		this.debouncedStopTyping();
	};

	sendMessageIfSocketExists = (message) => {
		const socketContext = this.socketProvider();
		const { sendMessage, socket } = socketContext;

		if (!sendMessage) return false;

		if (socket && socket.readyState !== WebSocket.OPEN) return false;

		try {
			const res = sendMessage(message);
			return res || true;
		} catch (e) {
			return false;
		}
	};

	resetJoinState = () => {
		this.joinSentForSocket = null;
		this.joinInFlightForSocket = null;
		this.joinPromise = null;
	};

	resetJoinStateIfSocketChanged = (socket) => {
		// If socket changed, we must re-join on the new socket.
		if (this.joinSentForSocket !== socket && this.joinInFlightForSocket !== socket) {
			this.resetJoinState();
		}
	};

	onSendNote = (message, newNoteType) => {
		const broadCastData = getNewNotePayload(this._getPayloadProps(), message, newNoteType);

		this.sendMessageIfSocketExists(broadCastData);
	};

	ensurePageEnter = () => {
		const socketContext = this.socketProvider();
		const { socket } = socketContext;

		if (!socket) return;

		// Reset state if socket has swapped
		this.resetJoinStateIfSocketChanged(socket);

		// Already successfully joined on this socket → do nothing
		if (this.joinSentForSocket === socket) return;

		// Join already being attempted for this socket → do nothing
		if (this.joinInFlightForSocket === socket && this.joinPromise) return;

		// Start join attempt
		this.joinInFlightForSocket = socket;

		const allEntityIds = this.getEntityIdsFromProps(this.component.props);
		const joinMessage = getPageEnterOrExitPayload(
			allEntityIds,
			WEB_SOCKET_ACTION_TYPES.JOIN,
			this.entityType
		);

		const result = this.sendMessageIfSocketExists(joinMessage);

		// Normalize to a promise so we can set "success" consistently
		this.joinPromise = Promise.resolve(result)
			.then((ok) => {
				// If sendMessage returns boolean, ok will be true/false.
				// If it returns undefined, our wrapper returns true.
				if (ok) {
					this.joinSentForSocket = socket;
				}
			})
			.catch(() => {
				// keep joinSentForSocket null so we can retry later
			})
			.finally(() => {
				// Only clear inflight if still same socket
				if (this.joinInFlightForSocket === socket) {
					this.joinInFlightForSocket = null;
					this.joinPromise = null;
				}
			});
	};

	checkScrollPosition = () => {
		const el = document.getElementById(this.chatBoxElementId);

		if (el) {
			const isScrolledToBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;

			if (isScrolledToBottom !== this.isScrolledToBottom) {
				this.isScrolledToBottom = isScrolledToBottom;
			}
		}

		this.rafId = requestAnimationFrame(this.checkScrollPosition);
	};

	onMount = () => {
		this.attachListener();
		this.startCleanup();
		this.setupVisibilityListener();
		this.ensurePageEnter();
		this.checkScrollPosition();
	};

	onUpdate = () => {
		this.attachListener();
	};

	onPageExit = () => {
		const allEntityIds = this.getEntityIdsFromProps(this.component.props);

		const leftMessage = getPageEnterOrExitPayload(
			allEntityIds,
			WEB_SOCKET_ACTION_TYPES.LEFT,
			this.entityType
		);

		this.resetJoinState();
		this.sendMessageIfSocketExists(leftMessage);
	};

	onUnmount = () => {
		this.onPageExit();
		const socketContext = this.socketProvider();
		const { socket } = socketContext;
		if (socket) {
			socket.removeEventListener('message', this.handleMessage);
		}
		this.previousSocket = null;
		this.stopCleanup();
		this.stopBlinkingTitle();
		this.cleanupVisibilityListener();
		cancelAnimationFrame(this.rafId);
	};
}
